/**
 * OrgSettingsContext — App-level Single Source of Truth for Locale & Formatting
 *
 * Mounts `useOrgSettings` once at the app root and exposes all parsed values
 * + formatter functions through React Context. Any component in the tree can
 * call `useOrgSettingsCtx()` to get org-driven currency, date, and number
 * formatting without triggering an additional Supabase query (React Query
 * already deduplicates, but context avoids even the hook overhead for leaf nodes).
 *
 * Usage:
 *   // In App.tsx — wrap after <OrganizationProvider>:
 *   <OrgSettingsProvider> ... </OrgSettingsProvider>
 *
 *   // In any component:
 *   const { fmtCurrency, fmtDate, currencyCode } = useOrgSettingsCtx();
 */

import { createContext, useContext, useLayoutEffect, useRef, type ReactNode } from 'react';
import { useOrgSettings, type UseOrgSettingsReturn } from '@/hooks/useOrgSettings';
import { useSetLanguage, languageLabelToCode } from './LanguageContext';
import { setActiveOrgCurrencySettings } from '@/modules/finance/utils/currencyFormatter';

// ─── Context ─────────────────────────────────────────────────────────────────

// Exported so a parallel entry (e.g. the public storefront) can supply a
// synthetic value derived from a per-host RPC instead of running the full
// `useOrgSettings` query path.
export const OrgSettingsContext = createContext<UseOrgSettingsReturn | undefined>(undefined);

// ─── Provider ────────────────────────────────────────────────────────────────

export function OrgSettingsProvider({ children }: { children: ReactNode }) {
  const settings = useOrgSettings();
  const setLanguageCode = useSetLanguage();

  const lastSyncedLang = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (!settings.isLoading && settings.language) {
      const code = languageLabelToCode(settings.language);
      if (code !== lastSyncedLang.current) {
        lastSyncedLang.current = code;
        setLanguageCode(code);
      }
    }
  }, [settings.language, settings.isLoading, setLanguageCode]);

  // Push org's currency + numberLocale into the finance currency formatter
  // module so non-React utility callers (e.g. `formatCurrency` invoked without
  // an explicit currency arg in FinancialReports / LedgerInquiry / FixedAssets)
  // produce thousand-separators per the user's "Number Format" setting.
  useLayoutEffect(() => {
    if (!settings.isLoading) {
      setActiveOrgCurrencySettings(settings.numberLocale, settings.currencyCode);
    }
  }, [settings.isLoading, settings.numberLocale, settings.currencyCode]);

  return (
    <OrgSettingsContext.Provider value={settings}>
      {children}
    </OrgSettingsContext.Provider>
  );
}

// ─── Consumer Hook ───────────────────────────────────────────────────────────

/**
 * Returns org-level locale settings and formatter functions from context.
 * MUST be used inside an `<OrgSettingsContext.Provider>` (i.e. either the
 * BMS `OrgSettingsProvider` or the storefront's `HostOrganizationProvider`).
 *
 * NOTE: an earlier draft of this file fell back to `useOrgSettings()` when
 * the context was missing. That violates the React Rules of Hooks because
 * the hook would only be called in some renders (the "context-defined"
 * vs "context-undefined" branches differ in hook count). Throwing keeps
 * hook order stable and surfaces the bug at component-mount time instead
 * of letting it explode mid-render.
 */
export function useOrgSettingsCtx(): UseOrgSettingsReturn {
  const ctx = useContext(OrgSettingsContext);
  if (!ctx) {
    throw new Error(
      "useOrgSettingsCtx must be used inside <OrgSettingsContext.Provider>. " +
        "BMS routes provide it via <OrgSettingsProvider>; the public storefront " +
        "provides it via <HostOrganizationProvider>.",
    );
  }
  return ctx;
}
